import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Shield, 
  Router, 
  Printer, 
  Flame, 
  Users, 
  Settings,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Clock,
  ArrowLeft,
  Home,
  LogOut
} from 'lucide-react';

const AdminLanding = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Handle logout
  const handleLogout = () => {
    if (logout) {
      logout();
    } else {
      // Fallback if logout not available in context
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    navigate('/');
  };

  // Admin email whitelist
  const ADMIN_EMAILS = ['booysena@gmail.com', 'andre@cnc-helpdesk.com'];

  // Check if user is admin
  const isAdmin = user && (
    user.is_admin || 
    user.is_super_admin || 
    (user.email && ADMIN_EMAILS.includes(user.email.toLowerCase()))
  );

  useEffect(() => {
    const fetchStats = async () => {
      if (!token || !isAdmin) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch admin stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [token, isAdmin]);

  // If not admin, show access denied
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center p-4">
        <Card className="bg-[#2c2c2c] border-red-500 max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
            <p className="text-zinc-400 mb-6">
              You don't have permission to access the admin area.
            </p>
            <Link to="/">
              <Button className="bg-[#e67e22] hover:bg-[#d35400]">
                <Home className="w-4 h-4 mr-2" />
                Return to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const modules = [
    {
      id: 'cnc_routing',
      title: 'CNC Router Admin',
      description: 'Manage router tools, materials, troubleshooting entries, and user data',
      icon: Router,
      link: '/admin/cnc-routing',
      color: 'from-[#e67e22]/20 to-[#e67e22]/5',
      borderColor: 'border-[#e67e22]',
      iconColor: 'text-[#e67e22]',
      features: ['User Management', 'Tool Database', 'Troubleshooting', 'Calculator Presets']
    },
    {
      id: '3d_printing',
      title: '3D Printing Admin',
      description: 'Manage printer profiles, materials, filament data, and print settings',
      icon: Printer,
      link: '/admin/3d-printing',
      color: 'from-orange-500/20 to-orange-500/5',
      borderColor: 'border-orange-500',
      iconColor: 'text-orange-500',
      features: ['Printer Presets', 'Material Database', 'Troubleshooting', 'AI Settings']
    },
    {
      id: 'laser_cutting',
      title: 'Laser Cutting Admin',
      description: 'Manage laser profiles, materials, safety data, and cut/engrave presets',
      icon: Flame,
      link: '/laser/admin',
      color: 'from-red-500/20 to-red-500/5',
      borderColor: 'border-red-500',
      iconColor: 'text-red-500',
      features: ['Laser Presets', 'Material Guide', 'Troubleshooting', 'Safety Warnings']
    }
  ];

  return (
    <div className="min-h-screen bg-[#1a1a1a] p-4 lg:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Link to="/" className="inline-flex items-center text-zinc-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#e67e22]/20 rounded-lg">
              <Shield className="w-8 h-8 text-[#e67e22]" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
              <p className="text-zinc-400">CNC-Helpdesk Administration Center</p>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-[#2c2c2c] border-zinc-700">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-zinc-500 uppercase">Total Users</p>
                    <p className="text-2xl font-bold text-white">{stats.total_users || 0}</p>
                  </div>
                  <Users className="w-8 h-8 text-blue-400 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#2c2c2c] border-zinc-700">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-zinc-500 uppercase">Pending Applications</p>
                    <p className="text-2xl font-bold text-yellow-400">{stats.pending || 0}</p>
                  </div>
                  <Clock className="w-8 h-8 text-yellow-400 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#2c2c2c] border-zinc-700">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-zinc-500 uppercase">Total Testers</p>
                    <p className="text-2xl font-bold text-green-400">{stats.total_testers || 0}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-400 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#2c2c2c] border-zinc-700">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-zinc-500 uppercase">Active Today</p>
                    <p className="text-2xl font-bold text-white">{stats.active_today || 0}</p>
                  </div>
                  <Settings className="w-8 h-8 text-zinc-400 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Global Admin Functions */}
        <Card className="bg-[#2c2c2c] border-zinc-700 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-[#e67e22]" />
              Global Administration
            </CardTitle>
            <CardDescription className="text-zinc-400">
              Manage users, tester applications, and system-wide settings
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link to="/admin/users">
                <div className="p-4 bg-[#1a1a1a] rounded-lg border border-zinc-700 hover:border-[#e67e22] transition-colors cursor-pointer group">
                  <Users className="w-6 h-6 text-blue-400 mb-2" />
                  <h3 className="font-medium text-white group-hover:text-[#e67e22]">User Management</h3>
                  <p className="text-xs text-zinc-500">View, edit, and manage all users</p>
                </div>
              </Link>
              <Link to="/admin/applications">
                <div className="p-4 bg-[#1a1a1a] rounded-lg border border-zinc-700 hover:border-[#e67e22] transition-colors cursor-pointer group">
                  <Clock className="w-6 h-6 text-yellow-400 mb-2" />
                  <h3 className="font-medium text-white group-hover:text-[#e67e22]">Tester Applications</h3>
                  <p className="text-xs text-zinc-500">Review and approve beta testers</p>
                  {stats?.pending > 0 && (
                    <Badge className="mt-2 bg-yellow-500">{stats.pending} pending</Badge>
                  )}
                </div>
              </Link>
              <Link to="/admin/settings">
                <div className="p-4 bg-[#1a1a1a] rounded-lg border border-zinc-700 hover:border-[#e67e22] transition-colors cursor-pointer group">
                  <Settings className="w-6 h-6 text-zinc-400 mb-2" />
                  <h3 className="font-medium text-white group-hover:text-[#e67e22]">System Settings</h3>
                  <p className="text-xs text-zinc-500">Configure system-wide options</p>
                </div>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Module Admin Cards */}
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5 text-[#e67e22]" />
          Module Administration
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {modules.map((module) => (
            <Link key={module.id} to={module.link}>
              <Card className={`bg-gradient-to-br ${module.color} border ${module.borderColor} hover:shadow-[0_0_20px_rgba(230,126,34,0.2)] transition-all cursor-pointer h-full`}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className={`p-2 bg-[#1a1a1a] rounded-lg`}>
                      <module.icon className={`w-6 h-6 ${module.iconColor}`} />
                    </div>
                    <CardTitle className="text-white">{module.title}</CardTitle>
                  </div>
                  <CardDescription className="text-zinc-400">
                    {module.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    {module.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-zinc-300">
                        <CheckCircle className="w-3 h-3 text-green-400" />
                        {feature}
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-1 text-[#e67e22] text-sm font-medium">
                    <span>Open Admin Panel</span>
                    <ChevronRight className="w-4 h-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-8 pt-8 border-t border-zinc-800 text-center">
          <p className="text-xs text-zinc-500">
            CNC-Helpdesk Admin • Logged in as {user?.email}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLanding;
